import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { flatten } from "lodash"
import moment from "moment-timezone"
import type { StateObject } from "../../../redux/types"
import type {
    NetCollectedByEmployeeDataType,
    NetCollectedByEmployeeDetailDataType,
    NetCollectedByPaymentTypeDataType,
    NetCollectedBySourceDataType,
    NetRevenueByAssignmentDataType,
    NetRevenueByCategoryDataType,
    SummaryGraphType,
    TransactionsByPaymentTypeDataType,
} from "./types"
import { ReportsPaymentType, SummaryGraphTypeEnum } from "./types"
import {
    getNetCollectedBreakdownByPaymentTypeData,
    getNetCollectedBreakdownData,
    getNetCollectedByEmployeeData,
    getNetCollectedByEmployeeDetailData,
    getNetCollectedBySourceData,
    getNetRevenueByAssignmentData,
    getNetRevenueByCategoryData,
    getReportDatapointTitle,
    getQueryFromDateOnly,
    getQueryToDateOnly,
    getSummarySelectedAssigneesIdsForHttpRequest,
    getTransactionsByPaymentTypeData,
} from "../selectors"
import { getFilters, getSelectedLocationIdsFromGlobalFilter, getTimestampFilter } from "../../../filters/selectors"
import { getCurrentAgenda } from "../../../router/selectors"
import { composeFullName } from "../../../../utils/general"
import type { TransactionsByPaymentTypeTableDataItem } from "./tables/TransactionsByPaymentTypeTable"
import type { TransactionsTableDataItem } from "./tables/TransactionsTable"
import type { NetCollectedByEmployeeTableDataItem } from "./tables/NetCollectedByEmployeeTable"
import type { NetCollectedByEmployeeDetailTableDataItem } from "./tables/NetCollectedByEmployeeDetailTable"
import type { NetRevenueByCategoryTableDataItem } from "./tables/NetRevenueByCategoryTable"
import type { NetRevenueByAssignmentTableDataItem } from "./tables/NetRevenueByAssignmentTable"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { MainMenuAgendaEnum } from "../../../router/types"
import type { NetCollectedBySourceTableDataItem } from "./tables/NetCollectedBySourceTable"
import { isUserAuthorizedForAction } from "../../roles/utils"
import { getAccesses } from "../../../data/selectors"
import { PermissionType } from "../../roles/selectors"
import { getSignedInUserAccount, getIsSuperUser } from "../../../auth/selectors"
import type { SummaryReportRequestData } from "../../../../graphql/types"
import { NetCollectedBreakdownTableDataItem } from "./tables/NetCollectedBreakdownTable"
import { NetCollectedBreakdownByPaymentTypeTableDataItem } from "./tables/NetCollectedBreakdownByPaymentTypeTable"
import { getArrayTotalById, getArrayTotalByName } from "../../employees/employees/utils"
import { IsoDateTime } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"

export const getArrayItemTotal = (arrayItem: Record<string, any>[]): number =>
    (arrayItem && arrayItem.length > 0 && arrayItem.map(i => i.total).reduce((a, b) => a + b, 0)) || 0

export const getDataForNetCollectedBreakdownTable = (state: StateObject): NetCollectedBreakdownTableDataItem[] => {
    const data = getNetCollectedBreakdownData(state)
    return [
        {
            name: "Gross Revenue",
            key: "gross_revenue",
            total: data.grossRevenue || 0,
            children: [
                {
                    name: "Voids",
                    key: "voids",
                    total: data.voidsTotal || 0,
                },
                {
                    name: "Refunds",
                    key: "refunds",
                    total: data.refundsTotal || 0,
                },
                {
                    name: "Special Pricings",
                    key: "special_pricings",
                    total: getArrayItemTotal(data.specialPricings),
                    children:
                        data.specialPricings && data.specialPricings.length > 0
                            ? data.specialPricings.map((specialPricing, idx) => ({
                                  name: specialPricing.name,
                                  key: specialPricing.id + idx,
                                  total: specialPricing.total,
                              }))
                            : undefined,
                },
                {
                    name: "Losses and Forfeits",
                    key: "losses_and_forfeits",
                    total: data.losses + data.forfeits || 0,
                },
            ],
        },
        {
            name: "Net Revenue",
            key: "net_revenue",
            total: data.netRevenue || 0,
            children: [
                {
                    name: "Taxes",
                    key: "taxes",
                    total: getArrayItemTotal(data.taxes),
                    children:
                        data.taxes && data.taxes.length > 0
                            ? data.taxes.map((tax, idx) => ({
                                  key: tax.name + idx,
                                  name: tax.name,
                                  total: tax.total,
                              }))
                            : undefined,
                },
                {
                    name: "Tips and Gratuity",
                    key: "tips_and_gratuity",
                    type: "tip",
                    total: data.tipsAndGratuityTotal || 0,
                    children: [
                        {
                            name: "Tips",
                            key: "tips",
                            type: "tip",
                            total: data.tipsTotal || 0,
                            children:
                                data.tips && data.tips.length > 1
                                    ? data.tips.map(tip => ({
                                          key: tip.id,
                                          name: tip.name,
                                          total: tip.amountTip,
                                          type: "tip",
                                      }))
                                    : undefined,
                        },
                        {
                            name: "Gratuity",
                            key: "_gratuity",
                            type: "tip",
                            total: data.gratuityTotal || 0,
                        },
                    ],
                },
                {
                    name: getReportDatapointTitle(state, "CARD_SURCHARGE_CUSTOM_TEXT", "Card Service Fee"),
                    key: "_surcharge",
                    total: data.cardSurcharge || 0,
                },
                {
                    name: getReportDatapointTitle(state, "CASH_DISCOUNT_CUSTOM_TEXT", "Cash Discount"),
                    key: "_cash_discount",
                    total: data.cashDiscount || 0,
                },
                {
                    name: getReportDatapointTitle(state, "SERVICE_FEE_CUSTOM_TEXT", "Service Fee"),
                    key: "_service_fee",
                    total: data.serviceFee || 0,
                },
            ],
        },
        ...(data.historicalRefunds + data.futureRefunds !== 0
            ? [
                  {
                      name: "Net Revenue + Taxes + Tips + Gratuity",
                      key: "net_revenue_taxes_tips_gratuity",
                      total: data.netRevenue + getArrayItemTotal(data.taxes) + data.tipsAndGratuityTotal || 0,
                      children: [
                          {
                              name: "Refunds - Historical",
                              key: "refunds_historical",
                              total: data.historicalRefunds || 0,
                          },
                          {
                              name: "Refunds - Post",
                              key: "refunds_post",
                              total: data.futureRefunds || 0,
                          },
                      ],
                  },
              ]
            : []),
        {
            name: "Net Collected",
            key: "net_collected",
            total: data.netCollected || 0,
        },
    ]
}

export const getComponentByType = (
    data: NetCollectedByPaymentTypeDataType,
    type: ReportsPaymentType
): Maybe<NetCollectedByPaymentTypeDataType> => {
    return data?.children?.find(c => c.paymentType === type)
}

export const getDataForNetCollectedBreakdownByPaymentTypeTable = (
    state: StateObject
): NetCollectedBreakdownByPaymentTypeTableDataItem[] => {
    const data: NetCollectedByPaymentTypeDataType = getNetCollectedBreakdownByPaymentTypeData(state)

    const cashComponent = getComponentByType(data, ReportsPaymentType.Cash)
    const cardComponent = getComponentByType(data, ReportsPaymentType.Card)
    const loyaltyComponent = getComponentByType(data, ReportsPaymentType.Redemption)
    const externalComponent = getComponentByType(data, ReportsPaymentType.External)
    const cardProgramComponent = getComponentByType(data, ReportsPaymentType.CardProgram)

    return [
        {
            name: "Gross Revenue",
            key: "gross_revenue",
            cashTotal: cashComponent?.grossRevenue || 0,
            cardTotal: cardComponent?.grossRevenue || 0,
            loyaltyTotal: loyaltyComponent?.grossRevenue || 0,
            externalTotal: externalComponent?.grossRevenue || 0,
            cardProgramTotal: cardProgramComponent?.grossRevenue || 0,
            total: data.grossRevenue || 0,
            children: [
                {
                    name: "Voids",
                    key: "voids",
                    cashTotal: cashComponent?.voidsTotal || 0,
                    cardTotal: cardComponent?.voidsTotal || 0,
                    loyaltyTotal: loyaltyComponent?.voidsTotal || 0,
                    externalTotal: externalComponent?.voidsTotal || 0,
                    cardProgramTotal: cardProgramComponent?.voidsTotal || 0,
                    total: data.voidsTotal || 0,
                },
                {
                    name: "Refunds",
                    key: "refunds",
                    cashTotal: cashComponent?.refundsTotal || 0,
                    cardTotal: cardComponent?.refundsTotal || 0,
                    loyaltyTotal: loyaltyComponent?.refundsTotal || 0,
                    externalTotal: externalComponent?.refundsTotal || 0,
                    cardProgramTotal: cardProgramComponent?.refundsTotal || 0,
                    total: data.refundsTotal || 0,
                },
                {
                    name: "Special Pricings",
                    key: "special_pricings",
                    total: getArrayItemTotal(data.specialPricings),
                    cashTotal: getArrayItemTotal(cashComponent?.specialPricings || []),
                    cardTotal: getArrayItemTotal(cardComponent?.specialPricings || []),
                    loyaltyTotal: getArrayItemTotal(loyaltyComponent?.specialPricings || []),
                    externalTotal: getArrayItemTotal(externalComponent?.specialPricings || []),
                    cardProgramTotal: getArrayItemTotal(cardProgramComponent?.specialPricings || []),
                    children:
                        data.specialPricings && data.specialPricings.length > 0
                            ? data.specialPricings.map((specialPricing, idx) => ({
                                  name: specialPricing.name,
                                  key: specialPricing.name + idx,
                                  cashTotal: getArrayTotalById(specialPricing.id, cashComponent?.specialPricings),
                                  cardTotal: getArrayTotalById(specialPricing.id, cardComponent?.specialPricings),
                                  loyaltyTotal: getArrayTotalById(specialPricing.id, loyaltyComponent?.specialPricings),
                                  externalTotal: getArrayTotalById(
                                      specialPricing.id,
                                      externalComponent?.specialPricings
                                  ),
                                  cardProgramTotal: getArrayTotalById(
                                      specialPricing.id,
                                      cardProgramComponent?.specialPricings
                                  ),
                                  total: specialPricing.total,
                              }))
                            : undefined,
                },
                {
                    name: "Losses and Forfeits",
                    key: "losses_and_forfeits",
                    cashTotal: (cashComponent?.losses || 0) + (cashComponent?.forfeits || 0),
                    cardTotal: (cardComponent?.losses || 0) + (cardComponent?.forfeits || 0),
                    loyaltyTotal: (loyaltyComponent?.losses || 0) + (loyaltyComponent?.forfeits || 0),
                    externalTotal: (externalComponent?.losses || 0) + (externalComponent?.forfeits || 0),
                    cardProgramTotal: (cardProgramComponent?.losses || 0) + (cardProgramComponent?.forfeits || 0),
                    total: data.losses + data.forfeits || 0,
                },
            ],
        },
        {
            name: "Net Revenue",
            key: "net_revenue",
            cashTotal: cashComponent?.netRevenue || 0,
            cardTotal: cardComponent?.netRevenue || 0,
            loyaltyTotal: loyaltyComponent?.netRevenue || 0,
            externalTotal: externalComponent?.netRevenue || 0,
            cardProgramTotal: cardProgramComponent?.netRevenue || 0,
            total: data.netRevenue || 0,
            children: [
                {
                    name: "Taxes",
                    key: "taxes",
                    cashTotal: getArrayItemTotal(cashComponent?.taxes || []),
                    cardTotal: getArrayItemTotal(cardComponent?.taxes || []),
                    loyaltyTotal: getArrayItemTotal(loyaltyComponent?.taxes || []),
                    externalTotal: getArrayItemTotal(externalComponent?.taxes || []),
                    cardProgramTotal: getArrayItemTotal(cardProgramComponent?.taxes || []),
                    total: getArrayItemTotal(data.taxes),
                    children:
                        data.taxes && data.taxes.length > 0
                            ? data.taxes.map((tax, idx) => ({
                                  key: tax.name + idx,
                                  name: tax.name,
                                  cashTotal: getArrayTotalByName(tax.name, cashComponent?.taxes),
                                  cardTotal: getArrayTotalByName(tax.name, cardComponent?.taxes),
                                  loyaltyTotal: getArrayTotalByName(tax.name, loyaltyComponent?.taxes),
                                  externalTotal: getArrayTotalByName(tax.name, externalComponent?.taxes),
                                  cardProgramTotal: getArrayTotalByName(tax.name, cardProgramComponent?.taxes),
                                  total: tax.total,
                              }))
                            : undefined,
                },
                {
                    name: "Tips and Gratuity",
                    key: "tips_and_gratuity",
                    type: "tip",
                    cashTotal: cashComponent?.tipsAndGratuityTotal || 0,
                    cardTotal: cardComponent?.tipsAndGratuityTotal || 0,
                    loyaltyTotal: loyaltyComponent?.tipsAndGratuityTotal || 0,
                    externalTotal: externalComponent?.tipsAndGratuityTotal || 0,
                    cardProgramTotal: cardProgramComponent?.tipsAndGratuityTotal || 0,
                    total: data.tipsAndGratuityTotal || 0,
                    children: [
                        {
                            name: "Tips",
                            key: "tips",
                            type: "tip",
                            cashTotal: cashComponent?.tipsTotal || 0,
                            cardTotal: cardComponent?.tipsTotal || 0,
                            loyaltyTotal: loyaltyComponent?.tipsTotal || 0,
                            externalTotal: externalComponent?.tipsTotal || 0,
                            cardProgramTotal: cardProgramComponent?.tipsTotal || 0,
                            total: data.tipsTotal || 0,
                            children:
                                data.tips && data.tips.length > 1
                                    ? data.tips.map(tip => ({
                                          key: tip.id,
                                          name: tip.name,
                                          cashTotal: getArrayTotalById(tip.id, cashComponent?.tips),
                                          cardTotal: getArrayTotalById(tip.id, cardComponent?.tips),
                                          loyaltyTotal: getArrayTotalById(tip.id, loyaltyComponent?.tips),
                                          externalTotal: getArrayTotalById(tip.id, externalComponent?.tips),
                                          cardProgramTotal: getArrayTotalById(tip.id, cardProgramComponent?.tips),
                                          total: tip.amountTip,
                                          type: "tip",
                                      }))
                                    : undefined,
                        },
                        {
                            name: "Gratuity",
                            key: "_gratuity",
                            type: "tip",
                            cashTotal: cashComponent?.gratuityTotal || 0,
                            cardTotal: cardComponent?.gratuityTotal || 0,
                            loyaltyTotal: loyaltyComponent?.gratuityTotal || 0,
                            externalTotal: externalComponent?.gratuityTotal || 0,
                            cardProgramTotal: cardProgramComponent?.gratuityTotal || 0,
                            total: data.gratuityTotal || 0,
                        },
                    ],
                },
                {
                    name: getReportDatapointTitle(state, "CARD_SURCHARGE_CUSTOM_TEXT", "Card Service Fee"),
                    key: "_surcharge",
                    cashTotal: cashComponent?.cardSurcharge || 0,
                    cardTotal: cardComponent?.cardSurcharge || 0,
                    loyaltyTotal: loyaltyComponent?.cardSurcharge || 0,
                    externalTotal: externalComponent?.cardSurcharge || 0,
                    cardProgramTotal: cardProgramComponent?.cardSurcharge || 0,
                    total: data.cardSurcharge || 0,
                },
                {
                    name: getReportDatapointTitle(state, "CASH_DISCOUNT_CUSTOM_TEXT", "Cash Discount"),
                    key: "_cash_discount",
                    cashTotal: cashComponent?.cashDiscount || 0,
                    cardTotal: cardComponent?.cashDiscount || 0,
                    loyaltyTotal: loyaltyComponent?.cashDiscount || 0,
                    externalTotal: externalComponent?.cashDiscount || 0,
                    cardProgramTotal: cardProgramComponent?.cashDiscount || 0,
                    total: data.cashDiscount || 0,
                },
                {
                    name: getReportDatapointTitle(state, "SERVICE_FEE_CUSTOM_TEXT", "Service Fee"),
                    key: "_service_fee",
                    cashTotal: cashComponent?.serviceFee || 0,
                    cardTotal: cardComponent?.serviceFee || 0,
                    loyaltyTotal: loyaltyComponent?.serviceFee || 0,
                    externalTotal: externalComponent?.serviceFee || 0,
                    cardProgramTotal: cardProgramComponent?.serviceFee || 0,
                    total: data.serviceFee || 0,
                },
            ],
        },
        ...(data.historicalRefunds + data.futureRefunds !== 0
            ? [
                  {
                      name: "Net Revenue + Taxes + Tips + Gratuity",
                      key: "net_revenue_taxes_tips_gratuity",
                      cashTotal:
                          (cashComponent?.netRevenue || 0) +
                          getArrayItemTotal(cashComponent?.taxes || []) +
                          (cashComponent?.tipsAndGratuityTotal || 0),
                      cardTotal:
                          (cardComponent?.netRevenue || 0) +
                          getArrayItemTotal(cardComponent?.taxes || []) +
                          (cardComponent?.tipsAndGratuityTotal || 0),
                      loyaltyTotal:
                          (loyaltyComponent?.netRevenue || 0) +
                          getArrayItemTotal(loyaltyComponent?.taxes || []) +
                          (loyaltyComponent?.tipsAndGratuityTotal || 0),
                      externalTotal:
                          (externalComponent?.netRevenue || 0) +
                          getArrayItemTotal(externalComponent?.taxes || []) +
                          (externalComponent?.tipsAndGratuityTotal || 0),
                      cardProgramTotal:
                          (cardProgramComponent?.netRevenue || 0) +
                          getArrayItemTotal(cardProgramComponent?.taxes || []) +
                          (cardProgramComponent?.tipsAndGratuityTotal || 0),
                      total: data.netRevenue + getArrayItemTotal(data.taxes) + data.tipsAndGratuityTotal || 0,
                      children: [
                          {
                              name: "Refunds - Historical",
                              key: "refunds_historical",
                              cashTotal: cashComponent?.historicalRefunds || 0,
                              cardTotal: cardComponent?.historicalRefunds || 0,
                              loyaltyTotal: loyaltyComponent?.historicalRefunds || 0,
                              externalTotal: externalComponent?.historicalRefunds || 0,
                              cardProgramTotal: cardProgramComponent?.historicalRefunds || 0,
                              total: data.historicalRefunds || 0,
                          },
                          {
                              name: "Refunds - Post",
                              key: "refunds_post",
                              cashTotal: cashComponent?.futureRefunds || 0,
                              cardTotal: cardComponent?.futureRefunds || 0,
                              loyaltyTotal: loyaltyComponent?.futureRefunds || 0,
                              externalTotal: externalComponent?.futureRefunds || 0,
                              cardProgramTotal: cardProgramComponent?.futureRefunds || 0,
                              total: data.futureRefunds || 0,
                          },
                      ],
                  },
              ]
            : []),
        {
            name: "Net Collected",
            key: "net_collected",
            cashTotal: cashComponent?.netCollected || 0,
            cardTotal: cardComponent?.netCollected || 0,
            loyaltyTotal: loyaltyComponent?.netCollected || 0,
            externalTotal: externalComponent?.netCollected || 0,
            cardProgramTotal: cardProgramComponent?.netCollected || 0,
            total: data.netCollected || 0,
        },
    ]
}

export const mapNetCollectedByEmployeeToTreeData = (
    items: NetCollectedByEmployeeDataType[]
): NetCollectedByEmployeeTableDataItem[] => {
    return items.map((item: NetCollectedByEmployeeDataType) => {
        const children = mapNetCollectedByEmployeeToTreeData(item.children)
        return {
            id: item.id || "total",
            name: composeFullName(item.firstName, item.surname),
            cashTotal: item.cashTotal,
            cardTotal: item.cardTotal,
            externalTotal: item.externalTotal,
            total: item.total,
            redemptionTotal: item.redemptionTotal,
            children: children.length > 0 ? children : null,
        }
    })
}

export const getDataForNetCollectedByEmployeeTable = (state: StateObject): NetCollectedByEmployeeTableDataItem[] => {
    const items = mapNetCollectedByEmployeeToTreeData(getNetCollectedByEmployeeData(state))
    const children = flatten(items.map(item => item.children || []))
    const rootItem =
        items && items[0] != null ? [{ ...items[0], name: "Net Collected", bottomRow: true, children: null }] : []
    return [...children, ...rootItem]
}

export const mapNetCollectedByEmployeeDetailToTreeData = (
    items: NetCollectedByEmployeeDetailDataType[]
): NetCollectedByEmployeeDetailTableDataItem[] => {
    return items.map((item: NetCollectedByEmployeeDetailDataType) => {
        const children = mapNetCollectedByEmployeeDetailToTreeData(item.children)
        return {
            id: item.id,
            name: composeFullName(item.firstName, item.surname),
            grossRevenueTotal: item.grossRevenueTotal,
            voidsTotal: item.voidsTotal,
            refundsTotal: item.refundsTotal,
            specialPricingsTotal: item.specialPricingsTotal,
            lossesTotal: item.lossesTotal,
            forfeitedTotal: item.forfeitedTotal,
            taxTotal: item.taxTotal,
            tipsTotal: item.tipsTotal,
            total: item.total,
            children: children.length > 0 ? children : null,
        }
    })
}

export const getNetCollectedByEmployeeDetailTotalRow = (
    items: NetCollectedByEmployeeDetailTableDataItem[]
): NetCollectedByEmployeeDetailTableDataItem => {
    return items.reduce(
        (accumulator: NetCollectedByEmployeeDetailTableDataItem, item: NetCollectedByEmployeeDetailTableDataItem) => {
            return {
                ...accumulator,
                grossRevenueTotal: accumulator.grossRevenueTotal + item.grossRevenueTotal,
                voidsTotal: accumulator.voidsTotal + item.voidsTotal,
                refundsTotal: accumulator.refundsTotal + item.refundsTotal,
                specialPricingsTotal: accumulator.specialPricingsTotal + item.specialPricingsTotal,
                lossesTotal: accumulator.lossesTotal + item.lossesTotal,
                forfeitedTotal: accumulator.forfeitedTotal + item.forfeitedTotal,
                taxTotal: accumulator.taxTotal + item.taxTotal,
                tipsTotal: accumulator.tipsTotal + item.tipsTotal,
                total: accumulator.total + item.total,
            }
        },
        {
            id: "total",
            name: "Total",
            grossRevenueTotal: 0,
            voidsTotal: 0,
            refundsTotal: 0,
            specialPricingsTotal: 0,
            lossesTotal: 0,
            forfeitedTotal: 0,
            taxTotal: 0,
            tipsTotal: 0,
            total: 0,
            children: null,
            // @ts-expect-error
            bottomRow: true,
        }
    )
}

export const getDataForNetCollectedByEmployeeDetailTable = (
    state: StateObject
): NetCollectedByEmployeeDetailTableDataItem[] => {
    const items = mapNetCollectedByEmployeeDetailToTreeData(getNetCollectedByEmployeeDetailData(state))
    const children = flatten(items.map(item => item.children || []))
    const totalRow = getNetCollectedByEmployeeDetailTotalRow(items)
    return [...children, totalRow]
}

export const mapNetRevenueByCategoryToTreeData = (
    items: NetRevenueByCategoryDataType[]
): NetRevenueByCategoryTableDataItem[] => {
    return items.map((item: NetRevenueByCategoryDataType) => {
        const children = mapNetRevenueByCategoryToTreeData(item.children || [])
        return {
            id: item.id + item.quantity + item.quantityPercentage + item.revenue + item.revenuePercentage,
            name: item.name,
            quantity: item.quantity,
            quantityPercentage: item.quantityPercentage,
            revenue: item.revenue,
            revenuePercentage: item.revenuePercentage,
            unit: item.unit,
            unitQuantity: item.unitQuantity,
            children: children && children.length > 0 ? children : null,
        }
    })
}

export const getDataForNetRevenueByCategoryTable = (state: StateObject): NetRevenueByCategoryTableDataItem[] => {
    const items = mapNetRevenueByCategoryToTreeData(getNetRevenueByCategoryData(state))
    const children = flatten(items.map(item => item.children || [])).map(c => ({
        ...c,
        children: c.children // Remove level 3 + children
            ? c.children.map(c2 => ({ ...c2, children: null }))
            : null,
    }))
    const rootItem =
        items && items[0]
            ? [
                  {
                      // @ts-expect-error
                      quantity: 0,
                      // @ts-expect-error
                      quantityPercentage: 0,
                      // @ts-expect-error
                      revenue: 0,
                      // @ts-expect-error
                      revenuePercentage: 0,
                      ...items[0],
                      id: "total",
                      name: "Net Revenue",
                      bottomRow: true,
                      children: null,
                  },
              ]
            : []
    return [...children, ...rootItem]
}

export const mapNetRevenueByAssignmentToTreeData = (
    items: NetRevenueByAssignmentDataType[]
): NetRevenueByAssignmentTableDataItem[] => {
    return items.map((item: NetRevenueByAssignmentDataType) => {
        const children = mapNetRevenueByAssignmentToTreeData(item.children)
        return {
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            quantityPercentage: item.quantityPercentage,
            revenue: item.revenue,
            revenuePercentage: item.revenuePercentage,
            children: children.length > 0 ? children : null,
        }
    })
}

export const getNetRevenueByAssignmentTotalRow = (
    items: NetRevenueByAssignmentTableDataItem[]
): NetRevenueByAssignmentTableDataItem => {
    return items.reduce(
        (accumulator: NetRevenueByAssignmentTableDataItem, item: NetRevenueByAssignmentTableDataItem) => {
            return {
                ...accumulator,
                quantity: accumulator.quantity + item.quantity,
                quantityPercentage: accumulator.quantityPercentage + item.quantityPercentage,
                revenue: accumulator.revenue + item.revenue,
                revenuePercentage: accumulator.revenuePercentage + item.revenuePercentage,
            }
        },
        {
            id: "total",
            name: "Net Collected",
            quantity: 0,
            quantityPercentage: 0,
            revenue: 0,
            revenuePercentage: 0,
            children: null,
            // @ts-expect-error
            bottomRow: true,
        }
    )
}

export const getDataForNetRevenueByAssignmentTable = (state: StateObject): NetRevenueByAssignmentTableDataItem[] => {
    const items = mapNetRevenueByAssignmentToTreeData(getNetRevenueByAssignmentData(state))
    const children = flatten(items.map(item => item.children || []))
    const totalRow = getNetRevenueByAssignmentTotalRow(items)
    return [...children, totalRow]
}

export const mapNetCollectedBySourceToTreeData = (
    items: NetCollectedBySourceDataType[]
): NetCollectedBySourceTableDataItem[] => {
    return items.map((item: NetCollectedBySourceDataType) => {
        const children = mapNetCollectedBySourceToTreeData(item.children)
        return {
            id: item.name + item.count + item.countPercentage + item.total + item.totalPercentage,
            name: item.name,
            count: item.count,
            countPercentage: item.countPercentage,
            total: item.total,
            totalPercentage: item.totalPercentage,
            children: children.length > 0 ? children : null,
        }
    })
}

export const getDataForNetCollectedBySourceTable = (state: StateObject): NetCollectedBySourceTableDataItem[] => {
    const items = mapNetCollectedBySourceToTreeData(getNetCollectedBySourceData(state))
    const children = flatten(items.map(item => item.children || []))
    const rootItem = items && items[0] ? [{ ...items[0], name: "Net Collected", bottomRow: true, children: null }] : []
    return [...children, ...rootItem]
}

export const mapTransactionsToTreeData = (items: TransactionsByPaymentTypeDataType[]): TransactionsTableDataItem[] => {
    return items.map((item: TransactionsByPaymentTypeDataType, idx) => {
        const children = mapTransactionsToTreeData(item.children)
        return {
            key: item.name + idx,
            name: item.name,
            creditsCount: item.creditsCount,
            debitsCount: item.debitsCount,
            netCount: item.netCount,
            children: children.length > 0 ? children : null,
        }
    })
}

export const getDataForTransactionsTable = (state: StateObject): TransactionsTableDataItem[] => {
    const items = mapTransactionsToTreeData(getTransactionsByPaymentTypeData(state))
    const children = flatten(items.map(item => item.children || []))
    const rootItem = items && items[0] ? [{ ...items[0], name: "Total", bottomRow: true, children: null }] : []
    return [...children, ...rootItem]
}

export const mapTransactionsByPaymentToTreeData = (
    items: TransactionsByPaymentTypeDataType[]
): TransactionsByPaymentTypeTableDataItem[] => {
    return items.map((item: TransactionsByPaymentTypeDataType, idx) => {
        const children = mapTransactionsByPaymentToTreeData(item.children)
        return {
            key: item.name + idx,
            name: item.name,
            count: item.netCount,
            percentage: item.percentage,
            total: item.total,
            children: children.length > 0 ? children : null,
        }
    })
}

export const getDataForTransactionsByPaymentTable = (state: StateObject): TransactionsByPaymentTypeTableDataItem[] => {
    const items = mapTransactionsByPaymentToTreeData(getTransactionsByPaymentTypeData(state))
    const children = flatten(items.map(item => item.children || []))
    const rootItem = items && items[0] ? [{ ...items[0], name: "Net Collected", bottomRow: true, children: null }] : []
    return [...children, ...rootItem]
}

export const isSummaryReportAgendaOpened = (state: StateObject) =>
    getCurrentAgenda(state) === MainMenuAgendaEnum.SummaryReport

export const getGlobalRangePickerRange = (state: StateObject): Maybe<{ from: IsoDateTime; to: IsoDateTime }> => {
    const utcTimestamp = getTimestampFilter(state)
    return utcTimestamp?.from && utcTimestamp?.to ? { from: utcTimestamp.from, to: utcTimestamp.to } : null
}

export const getSummaryQueryApiParamNew = (state: StateObject): SummaryReportRequestData => {
    const selectedLocationIds = getSelectedLocationIdsFromGlobalFilter(state)
    const selectedAssigneeIds: Maybe<string[]> = getSummarySelectedAssigneesIdsForHttpRequest(state)
    return {
        fromDate: getQueryFromDateOnly(state),
        toDate: getQueryToDateOnly(state),
        ...(selectedLocationIds.length > 0
            ? {
                  locationIds: selectedLocationIds,
              }
            : null),
        ...(selectedAssigneeIds != null
            ? {
                  assigneeIds: selectedAssigneeIds,
              }
            : null),
    }
}

export const getSummaryGraphMode = (state: StateObject): SummaryGraphType => {
    const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)

    if (globalFilters.date) {
        return Math.abs(moment(globalFilters.date.from).diff(globalFilters.date.to, "days")) === 0
            ? SummaryGraphTypeEnum.Hourly
            : SummaryGraphTypeEnum.Daily
    } else {
        return SummaryGraphTypeEnum.Hourly
    }
}

export const isLimitedAccessForDailySummary = (state: StateObject): boolean => {
    const userAccount = getSignedInUserAccount(state)

    if (!userAccount) {
        return false
    }

    return isUserAuthorizedForAction(userAccount, getAccesses(state), PermissionType.LimitToDsrAccess)
}

export const shouldShowBreakdownByPaymentType = (state: StateObject): boolean => {
    return getIsSuperUser(state)
}
